// hooks/academicPlan/usePlaceholderOperations.ts
/**
 * Placeholder operations hook
 * Manages creating, updating, and converting placeholder courses
 */
import { useCallback, useMemo } from 'react';
import {
  createPlaceholderCourse,
  type CreatePlaceholderOptions,
  type PlaceholderCourse
} from './helpers';
import { PlanActionType } from '../../adapters/plan';
import { useToast } from '../useToast';
import type { AcademicPlan, SemesterCode, EnrolledCourse } from '../../types';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Course input for conversion operations
 */
export interface ConvertCourseInput {
  code: string;
  title?: string;
  course_title?: string;
  credits?: number;
  [key: string]: unknown;
}

/**
 * Updates that can be applied to a placeholder
 */
export interface PlaceholderUpdates {
  title?: string;
  credits?: number;
  placeholderType?: string;
  requirements?: Record<string, unknown>;
  notes?: string;
  color?: string;
  [key: string]: unknown;
}

/**
 * Parameters for the usePlaceholderOperations hook
 */
export interface UsePlaceholderOperationsParams {
  setAcademicPlan: React.Dispatch<React.SetStateAction<AcademicPlan>>;
  addCourse: (course: ConvertCourseInput, semester: SemesterCode) => Promise<boolean>;
  addToBookmarks: (course: ConvertCourseInput) => Promise<void>;
  removeCourse: (semester: SemesterCode, courseIndex: number, skipConfirmation?: boolean) => Promise<void>;
  // Persistence deps (#205)
  currentPlanId: number | null;
  savePlanToBackend: (planId: number, action: string, data: Record<string, unknown>) => Promise<unknown>;
  refreshPlan: () => Promise<void>;
  academicPlan: AcademicPlan;
}

/**
 * Return type for the usePlaceholderOperations hook
 */
export interface PlaceholderOperations {
  createPlaceholder: (placeholderData: CreatePlaceholderOptions, targetSemester?: SemesterCode | null) => PlaceholderCourse;
  updatePlaceholder: (placeholderCode: string, updates: PlaceholderUpdates) => void;
  convertPlaceholder: (placeholderCode: string, realCourse: ConvertCourseInput, fromSemester: SemesterCode, fromIndex: number) => Promise<void>;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for placeholder operations within an academic plan
 */
export const usePlaceholderOperations = ({
  setAcademicPlan,
  addCourse,
  addToBookmarks,
  removeCourse,
  currentPlanId,
  savePlanToBackend,
  refreshPlan,
  academicPlan
}: UsePlaceholderOperationsParams): PlaceholderOperations => {
  const { showError } = useToast();
  // Create a new placeholder
  const createPlaceholder = useCallback((
    placeholderData: CreatePlaceholderOptions,
    targetSemester: SemesterCode | null = null
  ): PlaceholderCourse => {
    const placeholder = createPlaceholderCourse(placeholderData);

    if (targetSemester) {
      addCourse(placeholder as unknown as ConvertCourseInput, targetSemester);
    } else {
      addToBookmarks(placeholder as unknown as ConvertCourseInput);
    }

    return placeholder;
  }, [addCourse, addToBookmarks]);

  // Update an existing placeholder — optimistic local update plus the
  // update_placeholder persistence action (#205). The backend rejects a
  // code change, so the identity is stable across the round-trip.
  //
  // The persistence call is GATED on the placeholder living in a semester:
  // bookmark-shelf placeholders have no enrollment row, and calling anyway
  // would make the routine "not found" indistinguishable from a genuine
  // write failure. Because of the gate, any error that does arrive is real
  // and is surfaced to the user (a silently lost edit is invisible until
  // the next reload).
  const updatePlaceholder = useCallback((placeholderCode: string, updates: PlaceholderUpdates): void => {
    const inSemesters = Object.values(academicPlan?.semesters || {}).some(
      courses => (courses || []).some(c => c.code === placeholderCode && c.isPlaceholder)
    );
    if (currentPlanId && inSemesters) {
      const { code: _ignored, ...persistable } = updates;
      savePlanToBackend(currentPlanId, PlanActionType.UpdatePlaceholder, {
        placeholder_code: placeholderCode,
        updates: persistable
      }).catch((err: unknown) => {
        showError(
          `Failed to save placeholder changes: ${err instanceof Error ? err.message : String(err)}`
        );
      });
    }
    setAcademicPlan(prev => {
      if (!prev) return prev;

      const updatedPlan = { ...prev };
      const updatedSemesters = { ...updatedPlan.semesters };

      // Update in semesters
      Object.keys(updatedSemesters).forEach(semester => {
        updatedSemesters[semester] = updatedSemesters[semester].map(course => {
          if (course.code === placeholderCode && course.isPlaceholder) {
            return {
              ...course,
              ...updates,
              code: course.code,
              createdAt: (course as PlaceholderCourse).createdAt,
              updatedAt: new Date().toISOString()
            } as EnrolledCourse;
          }
          return course;
        });
      });

      // Update in bookmarks
      const updatedBookmarks = updatedPlan.bookmarkedCourses.map(course => {
        if (course.code === placeholderCode && course.isPlaceholder) {
          return {
            ...course,
            ...updates,
            code: course.code,
            createdAt: (course as PlaceholderCourse).createdAt,
            updatedAt: new Date().toISOString()
          } as EnrolledCourse;
        }
        return course;
      });

      return {
        ...updatedPlan,
        semesters: updatedSemesters,
        bookmarkedCourses: updatedBookmarks
      };
    });
  }, [setAcademicPlan, currentPlanId, savePlanToBackend, academicPlan, showError]);

  // Convert a placeholder to a real course. Persisted placeholders (#205)
  // convert via ONE backend action — routed through the normal add-course
  // machinery server-side, merging if the course is already in the semester
  // — then a refresh replaces optimistic state with truth. The pre-#205
  // remove+add fallback covers plans whose placeholder never persisted.
  const convertPlaceholder = useCallback(async (
    placeholderCode: string,
    realCourse: ConvertCourseInput,
    fromSemester: SemesterCode,
    fromIndex: number
  ): Promise<void> => {
    if (currentPlanId && placeholderCode.toLowerCase().startsWith('placeholder-')) {
      try {
        await savePlanToBackend(currentPlanId, PlanActionType.ConvertPlaceholder, {
          placeholder_code: placeholderCode,
          course_code: realCourse.code
        });
        await refreshPlan();
        return;
      } catch (err) {
        // "not found" = client-only placeholder (never persisted) — fall
        // through to the legacy remove+add path. Anything else: rethrow so
        // the modal surfaces the failure instead of silently dropping a slot.
        const message = err instanceof Error ? err.message : String(err);
        if (!message.toLowerCase().includes('not found')) {
          throw err;
        }
      }
    }

    // Legacy client-side conversion
    await removeCourse(fromSemester, fromIndex, true);
    await addCourse(realCourse, fromSemester);
  }, [removeCourse, addCourse, currentPlanId, savePlanToBackend, refreshPlan]);

  // Memoize operations object
  return useMemo(() => ({
    createPlaceholder,
    updatePlaceholder,
    convertPlaceholder
  }), [createPlaceholder, updatePlaceholder, convertPlaceholder]);
};
